import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Trash2,
  Save,
  X,
  Truck,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Edit2,
  MapPin,
  Building2,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import { shippingProviderService } from '@/services/shippingProviderService';
import { isCorreiosProviderName } from '@/utils/shippingProviders';
import { CorreiosIntegrationPanel } from './CorreiosIntegrationPanel';
import { FreightHubConfigPanel } from './FreightHubConfigPanel';
import {
  ShippingProvider,
  ShippingProviderAddress,
  ShippingProviderFormData,
  ShippingProviderService as ShippingProviderServiceModel,
} from '@/types/shippingProvider';
import { clsx } from 'clsx';

// ─── Tipos internos ─────────────────────────────────────────────

interface ProviderRow extends ShippingProvider {
  isExpanded: boolean;
  services: ServiceRow[];
  servicesLoaded: boolean;
  loadingServices: boolean;
}

interface ServiceRow extends ShippingProviderServiceModel {
  isEditing: boolean;
  tempName: string;
  tempInternalId: string;
  tempAgencyId: string;
  isSaving: boolean;
  errorMessage?: string;
}

const toServiceRow = (s: ShippingProviderServiceModel): ServiceRow => ({
  ...s,
  isEditing: false,
  tempName: s.provider_service_name,
  tempInternalId:
    s.provider_service_internal_id != null ? String(s.provider_service_internal_id) : '',
  tempAgencyId:
    s.provider_service_agency_id != null ? String(s.provider_service_agency_id) : '',
  isSaving: false,
});

const parseOptionalInteger = (
  value: string,
  label: string,
): { value: number | null; error?: string } => {
  const trimmed = value.trim();
  if (!trimmed) return { value: null };
  if (!/^\d+$/.test(trimmed)) {
    return { value: null, error: `${label} deve ser um número inteiro` };
  }
  return { value: Number(trimmed) };
};

const formatCep = (cep?: string | null): string => {
  if (!cep) return '';
  const cleaned = cep.replace(/\D/g, '').slice(0, 8);
  if (cleaned.length !== 8) return cep;
  return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
};

// ─── Modal: criar / editar transportadora completa ──────────────

interface ProviderFormModalProps {
  initial?: ShippingProvider | null;
  /** quando true, oculta o nome (apenas endereço). Usado pelo logistics_operator. */
  addressOnly?: boolean;
  onSave: (payload: ShippingProviderFormData | ShippingProviderAddress) => Promise<void>;
  onClose: () => void;
}

const ProviderFormModal: React.FC<ProviderFormModalProps> = ({
  initial,
  addressOnly = false,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(initial?.provider_name ?? '');
  const [street, setStreet] = useState(initial?.ship_addr_street ?? '');
  const [num, setNum] = useState(initial?.ship_addr_num ?? '');
  const [compl, setCompl] = useState(initial?.ship_addr_compl ?? '');
  const [district, setDistrict] = useState(initial?.ship_addr_district ?? '');
  const [city, setCity] = useState(initial?.ship_addr_city ?? '');
  const [state, setState] = useState(initial?.ship_addr_state ?? '');
  const [zip, setZip] = useState(initial?.ship_addr_zip ?? '');
  const [latitude, setLatitude] = useState(
    initial?.latitude != null ? String(initial.latitude) : '',
  );
  const [longitude, setLongitude] = useState(
    initial?.longitude != null ? String(initial.longitude) : '',
  );
  // New fiscal/document fields
  const [provDocument, setProvDocument] = useState(initial?.document ?? '');
  const [stateRegTaxId, setStateRegTaxId] = useState(initial?.state_registration_tax_id ?? '');
  const [fiscalAddress, setFiscalAddress] = useState(initial?.fiscal_address ?? '');
  const [fiscalCity, setFiscalCity] = useState(initial?.fiscal_city ?? '');
  const [fiscalState, setFiscalState] = useState(initial?.fiscal_state ?? '');
  const [hasVehicles, setHasVehicles] = useState(initial?.has_vehicles ?? false);
  const [isOwnCarrier, setIsOwnCarrier] = useState(initial?.is_own_carrier ?? false);
  // Teto de valor da NF-e desta transportadora. Vazio = sem limite; o backend recebe 0
  // como sentinela de "limpar o teto" (num campo opcional não dá para distinguir
  // "apagou" de "não enviou").
  const [maxInvoiceAmount, setMaxInvoiceAmount] = useState(
    initial?.max_invoice_amount != null ? String(initial.max_invoice_amount) : '',
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!initial;
  const title = addressOnly
    ? 'Editar endereço da transportadora'
    : isEdit
    ? 'Editar transportadora'
    : 'Nova transportadora';

  const handleSubmit = async () => {
    setError(null);
    if (!addressOnly && !name.trim()) {
      setError('Informe o nome da transportadora');
      return;
    }
    const lat = latitude.trim() ? parseFloat(latitude.replace(',', '.')) : null;
    const lng = longitude.trim() ? parseFloat(longitude.replace(',', '.')) : null;
    if (latitude.trim() && (lat === null || isNaN(lat) || lat < -90 || lat > 90)) {
      setError('Latitude inválida (deve estar entre -90 e 90)');
      return;
    }
    if (longitude.trim() && (lng === null || isNaN(lng) || lng < -180 || lng > 180)) {
      setError('Longitude inválida (deve estar entre -180 e 180)');
      return;
    }
    const cleanedZip = zip.replace(/\D/g, '');
    if (cleanedZip && cleanedZip.length !== 8) {
      setError('CEP deve conter 8 dígitos');
      return;
    }

    const addressPayload: ShippingProviderAddress = {
      ship_addr_street: street.trim() || null,
      ship_addr_num: num.trim() || null,
      ship_addr_compl: compl.trim() || null,
      ship_addr_district: district.trim() || null,
      ship_addr_city: city.trim() || null,
      ship_addr_state: state.trim().toUpperCase().slice(0, 2) || null,
      ship_addr_zip: cleanedZip || null,
      latitude: lat,
      longitude: lng,
    };

    const payload: ShippingProviderFormData | ShippingProviderAddress = addressOnly
      ? addressPayload
      : {
          provider_name: name.trim(),
          ...addressPayload,
          document: provDocument.trim() || null,
          state_registration_tax_id: stateRegTaxId.trim() || null,
          fiscal_address: fiscalAddress.trim() || null,
          fiscal_city: fiscalCity.trim() || null,
          fiscal_state: fiscalState.trim().toUpperCase().slice(0, 2) || null,
          has_vehicles: hasVehicles,
          is_own_carrier: isOwnCarrier,
          max_invoice_amount: maxInvoiceAmount.trim() === '' ? 0 : Number(maxInvoiceAmount),
        };

    try {
      setSaving(true);
      await onSave(payload);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Truck size={20} className="text-emerald-600" />
            <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {!addressOnly && (
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Nome da transportadora *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Transportadora ACME"
                maxLength={50}
                className="mt-1"
                autoFocus
              />
            </div>
          )}

          {/* Dados fiscais / documentais */}
          {!addressOnly && (
            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={16} className="text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-700">Dados Fiscais</h3>
              </div>
              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-12 md:col-span-6">
                  <label className="text-xs font-medium text-slate-600">CPF/CNPJ</label>
                  <Input
                    value={provDocument}
                    onChange={(e) => setProvDocument(e.target.value.replace(/\D/g, '').slice(0, 14))}
                    placeholder="00.000.000/0000-00"
                    maxLength={14}
                    className="mt-1"
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <label className="text-xs font-medium text-slate-600">Inscrição Estadual</label>
                  <Input
                    value={stateRegTaxId}
                    onChange={(e) => setStateRegTaxId(e.target.value)}
                    placeholder="Inscrição Estadual"
                    maxLength={20}
                    className="mt-1"
                  />
                </div>
                <div className="col-span-12 md:col-span-6">
                  <label className="text-xs font-medium text-slate-600">Endereço Fiscal</label>
                  <Input
                    value={fiscalAddress}
                    onChange={(e) => setFiscalAddress(e.target.value)}
                    placeholder="Logradouro fiscal completo"
                    maxLength={150}
                    className="mt-1"
                  />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <label className="text-xs font-medium text-slate-600">Cidade Fiscal</label>
                  <Input
                    value={fiscalCity}
                    onChange={(e) => setFiscalCity(e.target.value)}
                    placeholder="Município fiscal"
                    maxLength={100}
                    className="mt-1"
                  />
                </div>
                <div className="col-span-12 md:col-span-2">
                  <label className="text-xs font-medium text-slate-600">UF Fiscal</label>
                  <Input
                    value={fiscalState}
                    onChange={(e) => setFiscalState(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="SP"
                    maxLength={2}
                    className="mt-1 uppercase"
                  />
                </div>
                <div className="col-span-12">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasVehicles}
                      onChange={(e) => setHasVehicles(e.target.checked)}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Possui frota própria de veículos
                  </label>
                  <p className="text-xs text-slate-400 mt-1">
                    Quando marcado, a lista de veículos cadastrados será exibida nas telas de emissão de nota fiscal.
                  </p>
                </div>
                <div className="col-span-12">
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isOwnCarrier}
                      onChange={(e) => setIsOwnCarrier(e.target.checked)}
                      className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Transportadora própria (grupo Viver Organizer)
                  </label>
                  <p className="text-xs text-slate-400 mt-1">
                    Quando marcado, na NF-e os dados do transportador (CNPJ, nome, IE, município e UF) são
                    substituídos automaticamente pelos dados da empresa emitente da nota.
                  </p>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <label className="text-sm text-slate-700">Valor máximo da NF (R$)</label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={maxInvoiceAmount}
                    onChange={(e) => setMaxInvoiceAmount(e.target.value)}
                    placeholder="Sem limite"
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Transportadora de terceiros tem limite de valor declarado por volume (Correios:
                    R$ 10.000). Acima deste valor a emissão da nota é bloqueada e o pedido precisa ser
                    desmembrado. Deixe vazio para não ter limite — é o caso da transportadora própria.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} className="text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-700">Endereço de coleta</h3>
            </div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 md:col-span-3">
                <label className="text-xs font-medium text-slate-600">CEP</label>
                <Input
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="00000000"
                  maxLength={8}
                  className="mt-1"
                />
              </div>
              <div className="col-span-12 md:col-span-7">
                <label className="text-xs font-medium text-slate-600">Logradouro</label>
                <Input
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="col-span-12 md:col-span-2">
                <label className="text-xs font-medium text-slate-600">Número</label>
                <Input
                  value={num}
                  onChange={(e) => setNum(e.target.value)}
                  maxLength={10}
                  className="mt-1"
                />
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="text-xs font-medium text-slate-600">Complemento</label>
                <Input
                  value={compl}
                  onChange={(e) => setCompl(e.target.value)}
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <label className="text-xs font-medium text-slate-600">Bairro</label>
                <Input
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  maxLength={100}
                  className="mt-1"
                />
              </div>

              <div className="col-span-12 md:col-span-9">
                <label className="text-xs font-medium text-slate-600">Cidade</label>
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="col-span-12 md:col-span-3">
                <label className="text-xs font-medium text-slate-600">UF</label>
                <Input
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                  maxLength={2}
                  className="mt-1 uppercase"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} className="text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-700">Geolocalização</h3>
              <span className="text-xs text-slate-400">
                (usada para roteirização)
              </span>
            </div>
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 md:col-span-6">
                <label className="text-xs font-medium text-slate-600">Latitude</label>
                <Input
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="-23.550520"
                  className="mt-1 font-mono"
                />
              </div>
              <div className="col-span-12 md:col-span-6">
                <label className="text-xs font-medium text-slate-600">Longitude</label>
                <Input
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="-46.633308"
                  className="mt-1 font-mono"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg hover:bg-white disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw size={14} className="animate-spin" /> Salvando...
              </>
            ) : (
              <>
                <Save size={14} /> Salvar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: confirmar exclusão de transportadora ────────────────

interface DeleteProviderModalProps {
  provider: ShippingProvider;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error?: string;
}

const DeleteProviderModal: React.FC<DeleteProviderModalProps> = ({
  provider,
  onConfirm,
  onCancel,
  loading,
  error,
}) => {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const confirmed = typed.trim().toUpperCase() === provider.provider_name.toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={22} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Excluir transportadora</h2>
            <p className="text-sm text-slate-500 mt-1">
              Você está prestes a excluir{' '}
              <span className="font-semibold text-slate-800">{provider.provider_name}</span> e
              todos os seus serviços de frete. Esta ação não pode ser desfeita.
            </p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-red-700">
            Para confirmar, digite o <span className="font-semibold">nome da transportadora</span>:
          </p>
          <p className="font-mono text-sm font-bold text-red-800 tracking-wide bg-red-100 rounded-lg px-3 py-1.5">
            {provider.provider_name}
          </p>
          <input
            ref={inputRef}
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={`Digite ${provider.provider_name}`}
            className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white placeholder-red-300"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && confirmed) onConfirm();
              if (e.key === 'Escape') onCancel();
            }}
          />
        </div>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 border border-slate-300 text-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmed || loading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2.5 text-sm font-medium disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw size={14} className="animate-spin" /> Excluindo...
              </>
            ) : (
              <>
                <Trash2 size={14} /> Excluir
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-componente: linha de serviço (detail) ──────────────────

interface ServiceRowProps {
  row: ServiceRow;
  canWrite: boolean;
  onUpdate: (fields: Partial<ServiceRow>) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

const ServiceRowItem: React.FC<ServiceRowProps> = ({
  row,
  canWrite,
  onUpdate,
  onSave,
  onCancel,
  onDelete,
}) => {
  const editing = row.isEditing && canWrite;
  return (
    <tr className="bg-slate-50 border-b border-slate-100 text-sm">
      <td className="w-10" />
      <td className="px-4 py-2 font-mono text-xs text-slate-500">
        #{row.provider_service_id}
      </td>
      <td className="px-4 py-2">
        {editing ? (
          <input
            type="text"
            value={row.tempName}
            onChange={(e) => onUpdate({ tempName: e.target.value })}
            maxLength={50}
            className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSave();
              if (e.key === 'Escape') onCancel();
            }}
            autoFocus
          />
        ) : (
          <span className="text-slate-700">{row.provider_service_name}</span>
        )}
        {row.errorMessage && (
          <p className="text-red-500 text-xs mt-1">{row.errorMessage}</p>
        )}
      </td>
      <td className="px-4 py-2">
        {editing ? (
          <input
            type="text"
            inputMode="numeric"
            value={row.tempInternalId}
            onChange={(e) => onUpdate({ tempInternalId: e.target.value.replace(/\D/g, '') })}
            placeholder="Ex: 1"
            className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        ) : row.provider_service_internal_id != null ? (
          <span className="font-mono text-slate-700">{row.provider_service_internal_id}</span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-2">
        {editing ? (
          <input
            type="text"
            inputMode="numeric"
            value={row.tempAgencyId}
            onChange={(e) => onUpdate({ tempAgencyId: e.target.value.replace(/\D/g, '') })}
            placeholder="Ex: 2"
            className="w-full border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        ) : row.provider_service_agency_id != null ? (
          <span className="font-mono text-slate-700">{row.provider_service_agency_id}</span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        <div className="flex items-center justify-end gap-1">
          {editing ? (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={row.isSaving}
                className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                title="Salvar"
              >
                {row.isSaving ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={row.isSaving}
                className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-50"
                title="Cancelar"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            canWrite && (
              <>
                <button
                  type="button"
                  onClick={() => onUpdate({ isEditing: true })}
                  className="p-1 text-slate-400 hover:text-emerald-600"
                  title="Editar"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  className="p-1 text-slate-400 hover:text-red-500"
                  title="Excluir"
                >
                  <Trash2 size={14} />
                </button>
              </>
            )
          )}
        </div>
      </td>
    </tr>
  );
};

// ─── Sub-componente: form de novo serviço ───────────────────────

interface AddServiceFormProps {
  onAdd: (name: string, internalId: string, agencyId: string) => void;
  onCancel: () => void;
  saving: boolean;
  error?: string;
}

const AddServiceForm: React.FC<AddServiceFormProps> = ({ onAdd, onCancel, saving, error }) => {
  const [name, setName] = useState('');
  const [internalId, setInternalId] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onAdd(name.trim(), internalId, agencyId);
  };

  return (
    <tr className="bg-emerald-50 border-b border-emerald-100">
      <td colSpan={6} className="px-4 py-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
          <div>
            <label className="text-xs font-medium text-slate-600">Nome do serviço de frete *</label>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              placeholder="Ex: Sedex, PAC, Expresso..."
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
                if (e.key === 'Escape') onCancel();
              }}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">ID interno</label>
            <input
              type="text"
              inputMode="numeric"
              value={internalId}
              onChange={(e) => setInternalId(e.target.value.replace(/\D/g, ''))}
              placeholder="Opcional"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">ID agência</label>
            <input
              type="text"
              inputMode="numeric"
              value={agencyId}
              onChange={(e) => setAgencyId(e.target.value.replace(/\D/g, ''))}
              placeholder="Opcional"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!name.trim() || saving}
              className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-4 py-1.5 font-medium disabled:opacity-50"
            >
              {saving ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
              {saving ? 'Adicionando...' : 'Adicionar'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-300 rounded px-3 py-1.5"
            >
              <X size={12} /> Cancelar
            </button>
          </div>
        </div>
        {error && (
          <span className="mt-2 inline-flex text-red-500 text-xs items-center gap-1">
            <AlertCircle size={12} /> {error}
          </span>
        )}
      </td>
    </tr>
  );
};

// ─── View Principal ─────────────────────────────────────────────

export const ShippingProviderListView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWriteFull = checkPermission('shipping_providers.write').hasPermission;
  const canWriteAddress =
    canWriteFull || checkPermission('shipping_providers.write_address').hasPermission;
  const canCreate = checkPermission('shipping_providers.create').hasPermission;
  const canDelete = checkPermission('shipping_providers.delete').hasPermission;
  // Integração Correios por empresa (credenciais em texto claro): aba só para quem tem
  // shipping_integrations.read — nenhum perfil recebe por padrão, só admin.
  const canReadIntegrations = checkPermission('shipping_integrations.read').hasPermission;
  const canWriteIntegrations = checkPermission('shipping_integrations.write').hasPermission;
  // sub-aba do painel expandido do provider Correios: serviços × integração por empresa
  const [detailTab, setDetailTab] = useState<Record<number, 'services' | 'integration'>>({});
  // Hub de Fretes (configuração do motor de cotação FreightHub): aba de tela, acima da
  // tabela de transportadoras, só para quem tem freight_hub_config.read.
  const canReadFreightHub = checkPermission('freight_hub_config.read').hasPermission;
  const canWriteFreightHub = checkPermission('freight_hub_config.write').hasPermission;
  const [screenTab, setScreenTab] = useState<'providers' | 'hub'>('providers');

  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editProvider, setEditProvider] = useState<{
    provider: ShippingProvider;
    addressOnly: boolean;
  } | null>(null);

  const [deleteModal, setDeleteModal] = useState<ShippingProvider | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();

  // Adição inline de serviço
  const [addServiceForId, setAddServiceForId] = useState<number | null>(null);
  const [addServiceSaving, setAddServiceSaving] = useState(false);
  const [addServiceError, setAddServiceError] = useState<string | undefined>();

  // ── Load ─────────────────────────────────────────────────────

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await shippingProviderService.getProviders({
        page: currentPage,
        pageSize,
        search: search.trim() || undefined,
      });
      setProviders(
        result.data.map((p) => ({
          ...p,
          isExpanded: false,
          services: [],
          servicesLoaded: false,
          loadingServices: false,
        })),
      );
      setTotalPages(result.pagination.totalPages);
      setTotalRecords(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar transportadoras');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    const timer = setTimeout(() => setCurrentPage(1), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Toggle expand → carrega serviços ────────────────────────

  const toggleExpand = async (providerId: number) => {
    const target = providers.find((p) => p.provider_id === providerId);
    if (!target) return;

    if (target.isExpanded) {
      setProviders((prev) =>
        prev.map((p) => (p.provider_id === providerId ? { ...p, isExpanded: false } : p)),
      );
      return;
    }

    setProviders((prev) =>
      prev.map((p) =>
        p.provider_id === providerId
          ? { ...p, isExpanded: true, loadingServices: !p.servicesLoaded }
          : p,
      ),
    );

    if (!target.servicesLoaded) {
      try {
        const services = await shippingProviderService.getProviderServicesByProvider(providerId);
        setProviders((prev) =>
          prev.map((p) =>
            p.provider_id === providerId
              ? {
                  ...p,
                  services: services.map(toServiceRow),
                  servicesLoaded: true,
                  loadingServices: false,
                }
              : p,
          ),
        );
      } catch (err) {
        setProviders((prev) =>
          prev.map((p) =>
            p.provider_id === providerId
              ? { ...p, loadingServices: false, isExpanded: false }
              : p,
          ),
        );
        setError(err instanceof Error ? err.message : 'Erro ao carregar serviços');
      }
    }
  };

  // ── CRUD provider ────────────────────────────────────────────

  const handleCreateProvider = async (
    payload: ShippingProviderFormData | ShippingProviderAddress,
  ) => {
    const created = await shippingProviderService.createProvider(payload as ShippingProviderFormData);
    setProviders((prev) => [
      {
        ...created,
        isExpanded: false,
        services: [],
        servicesLoaded: false,
        loadingServices: false,
      },
      ...prev,
    ]);
    setTotalRecords((t) => t + 1);
  };

  const handleUpdateProvider = async (
    providerId: number,
    payload: ShippingProviderFormData | ShippingProviderAddress,
    addressOnly: boolean,
  ) => {
    const updated = addressOnly
      ? await shippingProviderService.updateProviderAddress(providerId, payload as ShippingProviderAddress)
      : await shippingProviderService.updateProvider(providerId, payload as ShippingProviderFormData);
    setProviders((prev) =>
      prev.map((p) => (p.provider_id === providerId ? { ...p, ...updated } : p)),
    );
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleteLoading(true);
    setDeleteError(undefined);
    try {
      await shippingProviderService.deleteProvider(deleteModal.provider_id);
      setProviders((prev) => prev.filter((p) => p.provider_id !== deleteModal.provider_id));
      setTotalRecords((t) => Math.max(0, t - 1));
      setDeleteModal(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Erro ao excluir');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── CRUD service (detail) ────────────────────────────────────

  const updateService = (providerId: number, serviceId: number, fields: Partial<ServiceRow>) => {
    setProviders((prev) =>
      prev.map((p) =>
        p.provider_id !== providerId
          ? p
          : {
              ...p,
              services: p.services.map((s) =>
                s.provider_service_id === serviceId ? { ...s, ...fields } : s,
              ),
            },
      ),
    );
  };

  const cancelEditService = (providerId: number, serviceId: number) => {
    setProviders((prev) =>
      prev.map((p) =>
        p.provider_id !== providerId
          ? p
          : {
              ...p,
              services: p.services.map((s) =>
                s.provider_service_id === serviceId
                  ? {
                      ...s,
                      isEditing: false,
                      tempName: s.provider_service_name,
                      tempInternalId:
                        s.provider_service_internal_id != null
                          ? String(s.provider_service_internal_id)
                          : '',
                      tempAgencyId:
                        s.provider_service_agency_id != null
                          ? String(s.provider_service_agency_id)
                          : '',
                      errorMessage: undefined,
                    }
                  : s,
              ),
            },
      ),
    );
  };

  const saveService = async (providerId: number, serviceId: number) => {
    const provider = providers.find((p) => p.provider_id === providerId);
    const service = provider?.services.find((s) => s.provider_service_id === serviceId);
    if (!service) return;
    if (!service.tempName.trim()) {
      updateService(providerId, serviceId, { errorMessage: 'Nome obrigatório' });
      return;
    }
    const internalId = parseOptionalInteger(service.tempInternalId, 'ID interno');
    if (internalId.error) {
      updateService(providerId, serviceId, { errorMessage: internalId.error });
      return;
    }
    const agencyId = parseOptionalInteger(service.tempAgencyId, 'ID agência');
    if (agencyId.error) {
      updateService(providerId, serviceId, { errorMessage: agencyId.error });
      return;
    }
    updateService(providerId, serviceId, { isSaving: true, errorMessage: undefined });
    try {
      const updated = await shippingProviderService.updateProviderService(serviceId, {
        provider_service_name: service.tempName.trim(),
        provider_id: providerId,
        provider_service_internal_id: internalId.value,
        provider_service_agency_id: agencyId.value,
      });
      updateService(providerId, serviceId, {
        ...toServiceRow(updated),
      });
    } catch (err) {
      updateService(providerId, serviceId, {
        isSaving: false,
        errorMessage: err instanceof Error ? err.message : 'Erro ao salvar',
      });
    }
  };

  const deleteService = async (providerId: number, serviceId: number) => {
    if (!window.confirm('Excluir este serviço de frete?')) return;
    try {
      await shippingProviderService.deleteProviderService(serviceId);
      setProviders((prev) =>
        prev.map((p) =>
          p.provider_id !== providerId
            ? p
            : { ...p, services: p.services.filter((s) => s.provider_service_id !== serviceId) },
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir serviço');
    }
  };

  const handleAddService = async (
    providerId: number,
    name: string,
    internalIdValue: string,
    agencyIdValue: string,
  ) => {
    setAddServiceSaving(true);
    setAddServiceError(undefined);
    const internalId = parseOptionalInteger(internalIdValue, 'ID interno');
    if (internalId.error) {
      setAddServiceSaving(false);
      setAddServiceError(internalId.error);
      return;
    }
    const agencyId = parseOptionalInteger(agencyIdValue, 'ID agência');
    if (agencyId.error) {
      setAddServiceSaving(false);
      setAddServiceError(agencyId.error);
      return;
    }
    try {
      const created = await shippingProviderService.createProviderService(providerId, {
        provider_service_name: name,
        provider_service_internal_id: internalId.value,
        provider_service_agency_id: agencyId.value,
      });
      setProviders((prev) =>
        prev.map((p) =>
          p.provider_id !== providerId
            ? p
            : { ...p, services: [...p.services, toServiceRow(created)] },
        ),
      );
      setAddServiceForId(null);
    } catch (err) {
      setAddServiceError(err instanceof Error ? err.message : 'Erro ao adicionar serviço');
    } finally {
      setAddServiceSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Modais */}
      {showCreateModal && (
        <ProviderFormModal
          onSave={handleCreateProvider}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      {editProvider && (
        <ProviderFormModal
          initial={editProvider.provider}
          addressOnly={editProvider.addressOnly}
          onSave={(payload) =>
            handleUpdateProvider(
              editProvider.provider.provider_id,
              payload,
              editProvider.addressOnly,
            )
          }
          onClose={() => setEditProvider(null)}
        />
      )}
      {deleteModal && (
        <DeleteProviderModal
          provider={deleteModal}
          loading={deleteLoading}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={() => {
            setDeleteModal(null);
            setDeleteError(undefined);
          }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Transportadoras</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Gerencie provedores de frete, endereços de coleta e serviços disponíveis
          </p>
        </div>
        {canCreate && screenTab === 'providers' && (
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus size={16} /> Nova Transportadora
          </Button>
        )}
      </div>

      {/* Abas da tela: transportadoras × Hub de Fretes */}
      {canReadFreightHub && (
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm">
          {(['providers', 'hub'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setScreenTab(tab)}
              className={clsx(
                'px-4 py-1.5 rounded-md font-medium',
                screenTab === tab ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100',
              )}
            >
              {tab === 'providers' ? 'Transportadoras' : 'Hub de Fretes'}
            </button>
          ))}
        </div>
      )}

      {screenTab === 'hub' && canReadFreightHub && <FreightHubConfigPanel canWrite={canWriteFreightHub} />}

      {screenTab === 'providers' && (
      <>
      {/* Filtro */}
      <Card>
        <CardContent className="pt-4">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
          <button className="ml-auto text-red-500 hover:text-red-700" onClick={loadProviders}>
            <RefreshCw size={16} />
          </button>
        </div>
      )}

      {/* Tabela */}
      <Card>
        <CardHeader
          title="Transportadoras"
          action={
            <span className="text-sm text-slate-500">
              {loading ? 'Carregando...' : `${totalRecords} transportadora(s)`}
            </span>
          }
        />
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-slate-400" />
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Truck size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhuma transportadora encontrada</p>
              {canCreate && (
                <button
                  className="mt-3 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                  onClick={() => setShowCreateModal(true)}
                >
                  Cadastrar a primeira transportadora
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 w-10" />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Transportadora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Endereço de coleta
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Geo
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide w-40">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map((p) => {
                    const addressText = [
                      p.ship_addr_street,
                      p.ship_addr_num,
                      p.ship_addr_district,
                      p.ship_addr_city && p.ship_addr_state
                        ? `${p.ship_addr_city}/${p.ship_addr_state}`
                        : p.ship_addr_city || p.ship_addr_state,
                      formatCep(p.ship_addr_zip),
                    ]
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <React.Fragment key={p.provider_id}>
                        <tr
                          className={clsx(
                            'border-b border-slate-200 transition-colors',
                            p.isExpanded ? 'bg-emerald-50' : 'bg-white hover:bg-slate-50',
                          )}
                        >
                          <td className="px-4 py-3 w-10">
                            <button
                              type="button"
                              onClick={() => toggleExpand(p.provider_id)}
                              className="p-1 rounded hover:bg-slate-200 text-slate-500"
                              title={p.isExpanded ? 'Recolher' : 'Expandir'}
                            >
                              {p.loadingServices ? (
                                <RefreshCw size={16} className="animate-spin" />
                              ) : p.isExpanded ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            <div className="flex items-center gap-2">
                              <Building2 size={15} className="text-slate-400" />
                              {p.provider_name}
                              <span className="text-xs text-slate-400 font-normal">
                                #{p.provider_id}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 max-w-md">
                            {addressText ? (
                              <span className="truncate block" title={addressText}>
                                {addressText}
                              </span>
                            ) : (
                              <span className="text-slate-400 italic">sem endereço</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {p.latitude != null && p.longitude != null ? (
                              <span
                                className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-medium"
                                title={`${p.latitude}, ${p.longitude}`}
                              >
                                <MapPin size={11} /> OK
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {canWriteAddress && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditProvider({
                                      provider: p,
                                      addressOnly: !canWriteFull,
                                    })
                                  }
                                  className="p-1.5 text-slate-400 hover:text-emerald-600"
                                  title={
                                    canWriteFull
                                      ? 'Editar transportadora'
                                      : 'Editar endereço/geolocalização'
                                  }
                                >
                                  <Edit2 size={16} />
                                </button>
                              )}
                              {canDelete && (
                                <button
                                  type="button"
                                  onClick={() => setDeleteModal(p)}
                                  className="p-1.5 text-slate-400 hover:text-red-500"
                                  title="Excluir"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {p.isExpanded && isCorreiosProviderName(p.provider_name) && canReadIntegrations && (
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <td className="w-10" />
                            <td colSpan={4} className="px-4 py-2">
                              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs">
                                {(['services', 'integration'] as const).map((tab) => {
                                  const active = (detailTab[p.provider_id] ?? 'services') === tab;
                                  return (
                                    <button
                                      key={tab}
                                      type="button"
                                      onClick={() => setDetailTab((prev) => ({ ...prev, [p.provider_id]: tab }))}
                                      className={clsx(
                                        'px-3 py-1 rounded-md font-medium',
                                        active ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-100',
                                      )}
                                    >
                                      {tab === 'services' ? 'Serviços' : 'Integração por empresa'}
                                    </button>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}

                        {p.isExpanded &&
                          isCorreiosProviderName(p.provider_name) &&
                          canReadIntegrations &&
                          detailTab[p.provider_id] === 'integration' && (
                            <tr className="bg-white border-b border-slate-200">
                              <td className="w-10" />
                              <td colSpan={4} className="px-4 py-3">
                                <CorreiosIntegrationPanel canWrite={canWriteIntegrations} />
                              </td>
                            </tr>
                          )}

                        {p.isExpanded && detailTab[p.provider_id] !== 'integration' && (
                          <>
                            <tr className="bg-slate-100 border-b border-slate-200">
                              <td className="w-10" />
                              <th className="px-4 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                ID
                              </th>
                              <th className="px-4 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Serviço de frete
                              </th>
                              <th className="px-4 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                ID interno
                              </th>
                              <th className="px-4 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                ID agência
                              </th>
                              <th className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                {canWriteFull && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setAddServiceForId(p.provider_id);
                                      setAddServiceError(undefined);
                                    }}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 font-medium normal-case tracking-normal flex items-center gap-1 ml-auto"
                                  >
                                    <Plus size={12} /> Novo serviço
                                  </button>
                                )}
                              </th>
                            </tr>

                            {addServiceForId === p.provider_id && (
                              <AddServiceForm
                                onAdd={(name, internalId, agencyId) =>
                                  handleAddService(p.provider_id, name, internalId, agencyId)
                                }
                                onCancel={() => {
                                  setAddServiceForId(null);
                                  setAddServiceError(undefined);
                                }}
                                saving={addServiceSaving}
                                error={addServiceError}
                              />
                            )}

                            {p.services.length === 0 && !p.loadingServices && addServiceForId !== p.provider_id ? (
                              <tr className="bg-slate-50 border-b border-slate-100">
                                <td
                                  colSpan={6}
                                  className="px-4 py-4 text-center text-slate-400 text-sm italic"
                                >
                                  Nenhum serviço de frete cadastrado
                                </td>
                              </tr>
                            ) : (
                              p.services.map((s) => (
                                <ServiceRowItem
                                  key={s.provider_service_id}
                                  row={s}
                                  canWrite={canWriteFull}
                                  onUpdate={(fields) =>
                                    updateService(p.provider_id, s.provider_service_id, fields)
                                  }
                                  onSave={() => saveService(p.provider_id, s.provider_service_id)}
                                  onCancel={() =>
                                    cancelEditService(p.provider_id, s.provider_service_id)
                                  }
                                  onDelete={() =>
                                    deleteService(p.provider_id, s.provider_service_id)
                                  }
                                />
                              ))
                            )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs text-slate-500">
              <span>
                Página {currentPage} de {totalPages} — {totalRecords} registro(s)
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1 || loading}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  disabled={currentPage === totalPages || loading}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
                >
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissão denegada (sem read) */}
      <PermissionGuard
        permission="shipping_providers.read"
        fallback={
          <div className="text-center py-12 text-slate-500">
            <span className="text-sm">Você não tem permissão para visualizar transportadoras.</span>
          </div>
        }
      >
        <></>
      </PermissionGuard>
      </>
      )}
    </div>
  );
};

export default ShippingProviderListView;
